'use client'

import { ArrowRight, BookOpenText } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { formatDateNormal, formatImageURL } from '@/utils/util'
import { getReadingTime } from '@/utils/blog'
import { navigateUrl } from '@/utils/client-request'
import BlogCard from '../blog/BlogCard'

function FeatureTile({ blog }: { blog: any }) {
  const pathname = usePathname()
  const href = navigateUrl(`/blog/${blog.id}`, pathname)
  const category = blog.category?.name || blog.category || ''
  const reading = getReadingTime(blog.content)
  return (
    <a
      href={href}
      className="relative rounded-3xl overflow-hidden aspect-16/10 block group cursor-pointer"
      style={{ border: '1px solid rgba(255,255,255,0.13)' }}
    >
      <img
        src={formatImageURL(blog.thumbnail || 'placeholder.png')}
        alt={blog.title}
        className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out will-change-transform group-hover:scale-105"
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(110deg, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.2) 70%)' }} />
      <div className="relative p-8 sm:p-10 h-full flex flex-col justify-end">
        {category && (
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>
            Блог · {category}
          </div>
        )}
        <h3
          className="mt-2 font-semibold text-white tracking-[-0.02em] leading-[1.05] max-w-md line-clamp-3"
          style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 32 }}
        >
          {blog.title}
        </h3>
        <div className="mt-3 flex items-center gap-2 text-[12px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
          <span className="tabular-nums">{formatDateNormal(new Date(blog.createdAt))}</span>
          {reading > 0 && (
            <>
              <span style={{ opacity: 0.5 }}>·</span>
              <span className="tabular-nums">{reading} мин</span>
            </>
          )}
        </div>
        <div className="mt-5">
          <span
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-medium text-white backdrop-blur"
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)' }}
          >
            Бүрэн унших <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </a>
  )
}

export default function Blogs({ blogs, title, limit, description }: { blogs: any[], title?: string, limit?: number, description?: string }) {
  const pathname = usePathname()
  const list = blogs || []
  const featureCount = Math.min(limit || 2, list.length)
  const featured = list.slice(0, featureCount)
  const sectionTitle = title || 'Нийтлэл'
  const sectionDesc = description || 'Манай дэлгүүрийн шинэ мэдээлэл болон мэргэжлийн зөвлөмжүүдийг уншина уу.'

  return (
    <div className="cont py-10">
      <div className="flex items-end justify-between gap-8 mb-7">
        <div className="max-w-2xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-2.5" style={{ color: '#a8a8b3' }}>Блог</div>
          <h2
            className="text-2xl sm:text-[34px] font-semibold tracking-[-0.02em] leading-[1.05]"
            style={{ fontFamily: 'Gilroy, Inter, sans-serif', color: 'white' }}
          >
            {sectionTitle}
          </h2>
          {sectionDesc && (
            <p className="mt-3 text-[14px] leading-6 max-w-lg" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {sectionDesc}
            </p>
          )}
        </div>
        <a
          href={navigateUrl('/blog', pathname)}
          className="hidden sm:inline-flex text-[13px] font-medium items-center gap-1.5 px-4 h-9 rounded-full shrink-0"
          style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          Бүх нийтлэл <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>

      {featured.length > 0 ? (
        featured.length <= 2 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {featured.map((blog) => <FeatureTile key={blog.id} blog={blog} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10">
            {featured.map((blog, i) => <BlogCard key={blog.id} blog={blog} index={i} />)}
          </div>
        )
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-16" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <BookOpenText className="h-8 w-8" />
          <div className="text-[14px]">Нийтлэл хоосон байна</div>
        </div>
      )}

      <div className="flex items-center justify-center sm:hidden mt-10">
        <a
          href={navigateUrl('/blog', pathname)}
          className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-[13px] font-medium w-full"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.14)' }}
        >
          Бүх нийтлэл <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  )
}
